// Typed wrappers for /v1/merchants/:id/fee-rules (migration 041).
//
// SUPERADMIN ONLY on the backend (auth.RequireSuperadmin — which refuses
// plain admins, unlike every other admin gate in the app). The panel hides
// the screen for non-superadmins; the server is the authority.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient.
import { apiClient } from "./client";

/**
 * FeeKind — the three chargeable events.
 *
 *   "topup"       — an operator credits the merchant's ledger. Basis is the
 *                   credited amount in paise.
 *   "transaction" — a payout succeeded. Basis is the payout amount.
 *   "beneficiary" — monthly, per distinct beneficiary actually paid. Basis
 *                   is a COUNT, not an amount, and percent_bp must be 0.
 */
export type FeeKind = "topup" | "transaction" | "beneficiary";

export const FEE_KINDS: FeeKind[] = ["topup", "transaction", "beneficiary"];

/**
 * FeeRule — one band of a merchant's step scale.
 *
 * The band is half-open: [basis_from_minor, basis_to_minor). A null
 * basis_to_minor means "and up". That is what makes a pricing sheet reading
 * "1,000 to 10,000 / 10,000 to 100,000" put ₹10,000 in exactly one band.
 *
 * Charge for the matched band:
 *   raw    = flat_minor + floor(basis * percent_bp / 10000)
 *   charge = clamp(raw, min_fee_minor, max_fee_minor)
 *
 * percent_bp is basis points — 250 = 2.50%. Integer on purpose: a float
 * percentage would reintroduce exactly the rounding drift the
 * paise-everywhere convention exists to prevent.
 *
 * NO band covering a basis means NO fee. A kind with zero rules is off.
 */
export interface FeeRule {
  id: number;
  merchant_id: number;
  kind: FeeKind;
  basis_from_minor: number;
  basis_to_minor: number | null;
  flat_minor: number;
  percent_bp: number;
  min_fee_minor: number | null;
  max_fee_minor: number | null;
  currency: string;
  created_at: string;
  updated_at: string;
}

/** One band on the way in. No id — the whole scale is replaced. */
export interface FeeRuleInput {
  basis_from_minor: number;
  basis_to_minor: number | null;
  flat_minor: number;
  percent_bp: number;
  min_fee_minor: number | null;
  max_fee_minor: number | null;
}

/** listFeeRules — GET /v1/merchants/:id/fee-rules. Every kind, one call. */
export async function listFeeRules(
  merchantId: number | string,
): Promise<FeeRule[]> {
  const resp = await apiClient.get<{ fee_rules: FeeRule[] }>(
    `/v1/merchants/${merchantId}/fee-rules`,
  );
  return resp.data.fee_rules;
}

/**
 * replaceFeeRules — PUT /v1/merchants/:id/fee-rules.
 *
 * Replaces the ENTIRE scale for one kind in a single transaction. A scale is
 * only meaningful as a whole — bands have to tile the range the way you
 * intended — so there is deliberately no per-band PATCH that could leave a
 * merchant charged from a half-applied edit.
 *
 * An empty `rules` array turns the fee off for that kind.
 */
export async function replaceFeeRules(
  merchantId: number | string,
  kind: FeeKind,
  rules: FeeRuleInput[],
): Promise<FeeRule[]> {
  const resp = await apiClient.put<{ fee_rules: FeeRule[] }>(
    `/v1/merchants/${merchantId}/fee-rules`,
    { kind, rules },
  );
  return resp.data.fee_rules;
}

/**
 * previewCharge mirrors internal/fees Match + Compute so the editor can show
 * what a given basis would actually cost BEFORE the scale is saved.
 *
 * Kept deliberately faithful to the Go, including floor rounding and
 * most-specific-band-wins, because a preview that disagrees with the charge
 * is worse than no preview: it would be used to sign off pricing that then
 * bills something else. Returns null when no band covers the basis — that's
 * "not charged", not zero.
 */
export function previewCharge(
  rules: FeeRuleInput[],
  kind: FeeKind,
  basis: number,
): number | null {
  const covering = rules.filter(
    (r) =>
      basis >= r.basis_from_minor &&
      (r.basis_to_minor == null || basis < r.basis_to_minor),
  );
  if (covering.length === 0) return null;

  // Most specific wins: highest lower bound, then the tighter upper bound.
  // Matches fees.Match — picking by array order would make the preview
  // disagree with the server whenever bands overlap.
  covering.sort((a, b) => {
    if (a.basis_from_minor !== b.basis_from_minor) {
      return b.basis_from_minor - a.basis_from_minor;
    }
    const aUnbounded = a.basis_to_minor == null;
    const bUnbounded = b.basis_to_minor == null;
    if (aUnbounded !== bUnbounded) return aUnbounded ? 1 : -1;
    if (a.basis_to_minor != null && b.basis_to_minor != null) {
      return a.basis_to_minor - b.basis_to_minor;
    }
    return 0;
  });
  const r = covering[0];

  // Beneficiary: the basis is a headcount and the flat component is per head.
  const raw =
    kind === "beneficiary"
      ? r.flat_minor * basis
      : r.flat_minor + Math.floor((basis * r.percent_bp) / 10000);

  let out = Math.max(0, raw);
  if (r.min_fee_minor != null && out < r.min_fee_minor) out = r.min_fee_minor;
  if (r.max_fee_minor != null && out > r.max_fee_minor) out = r.max_fee_minor;
  return out;
}
